import { ConversationDetailsInner } from "@/components/conversation-details";
import { Header } from "@/components/conversation-details/header";
import { Hub } from "@/components/hub/hub";
import { HubStoreProvider, useHubStore } from "@/components/hub/use-hub-store";
import { Conversation } from "@/components/mentorships/details/conversation.tsx";
import { MentorshipDetails, useGetMentorship } from "@/lib/api/mentorships.ts";
import { useUser } from "@/lib/api/user";
import { useMentorView } from "@/lib/hooks/admin/use-mentor-view";
import { useDetailsSidebarState } from "@/lib/hooks/conversations/use-details-sidebar-state";
import { cn } from "@/lib/utils";
import { requirePermission } from "@/lib/casl/route-guards";
import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";

const searchSchema = z.object({
  messageId: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute(
  "/_authenticated/mentorships/$mentorshipId",
)({
  component: Mentorship,
  validateSearch: searchSchema,
  beforeLoad: ({ context }) => {
    requirePermission(context.user, "read", "mentorships");
  },
});

function Mentorship() {
  const { mentorshipId } = useParams({
    strict: false,
  }) as { mentorshipId: string };
  const { messageId: focusedMessageId } = useSearch({
    from: "/_authenticated/mentorships/$mentorshipId",
  });

  const adminMode = useMentorView((state) => !state.mentorView);

  const {
    data: mentorship,
    isPending,
    isError,
  } = useGetMentorship(mentorshipId);
  const { data: mentor } = useUser(mentorship?.mentor.id ?? "");
  const { data: mentee } = useUser(mentorship?.mentee.id ?? "");

  const enrichedMentorship = useMemo(() => {
    if (!mentorship) return mentorship;

    return {
      ...mentorship,
      mentor: {
        ...mentorship.mentor,
        systemRoles: mentorship.mentor.systemRoles ?? mentor?.systemRoles,
      },
      mentee: {
        ...mentorship.mentee,
        systemRoles: mentorship.mentee.systemRoles ?? mentee?.systemRoles,
      },
    };
  }, [mentee, mentor, mentorship]);

  if (isPending) {
    return (
      <div className="h-screen-nav bg-primary-100 flex h-screen w-full items-center justify-center">
        <Loader2 className="text-blue-brand h-12 w-12 animate-spin" />
      </div>
    );
  }

  if (isError || !enrichedMentorship) {
    return (
      <div className="h-screen-nav bg-primary-100 flex h-screen w-full items-center justify-center">
        <div className="text-blue-brand">Some errors occurred!</div>
      </div>
    );
  }

  return (
    <HubStoreProvider>
      <div className="flex h-screen">
        <div className="flex-1">
          <Conversation
            mentorshipId={mentorshipId}
            mentorship={enrichedMentorship}
            focusedMessageId={focusedMessageId}
          />
        </div>
        <ConversationDetailsPanel
          mentorship={enrichedMentorship}
          isPending={isPending}
          adminMode={adminMode || false}
        />
      </div>
    </HubStoreProvider>
  );
}

interface ConversationDetailsPanelProps {
  mentorship: MentorshipDetails;
  isPending: boolean;
  adminMode: boolean;
}

const ConversationDetailsPanel = ({
  mentorship,
  isPending,
  adminMode,
}: ConversationDetailsPanelProps) => {
  const { isCollapsed, onToggleCollapse } = useDetailsSidebarState();
  const isHubOpen = useHubStore((state) => state.isOpen);

  return (
    <div
      className={cn(
        "border-l transition-all duration-300",
        isHubOpen
          ? "w-full lg:w-[500px] xl:w-[600px]"
          : isCollapsed
            ? "w-[70px]"
            : "w-[450px]",
      )}
    >
      {isHubOpen ? (
        <Hub mentorship={mentorship} />
      ) : (
        <div className="flex h-full flex-col">
          <Header
            isCollapsed={isCollapsed}
            onToggleCollapse={onToggleCollapse}
          />
          <ConversationDetailsInner
            menteeId={mentorship.mentee.id}
            mentorId={mentorship.mentor.id}
            adminMode={adminMode}
            mentorship={mentorship}
            isMentorshipsLoading={isPending}
            isCollapsed={isCollapsed}
          />
        </div>
      )}
    </div>
  );
};
